import { Box, Checkbox, Chip, CircularProgress, Container, Grid, MenuItem, Select, Typography } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import { usePatchUser, useUserSearchQuery } from '@src/queries/UserManagementQuery';
import { useSearchContext } from '@src/contexts/SearchContextProvider';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { CognitoGroup, UserInfo, isAuthError } from '@src/common-utils/AuthModels';
import { Nullable } from '@src/common-utils/Models';
import { useUserContext } from '@src/contexts/UserContextProvider';
import { ROUTES } from '@src/layout/RoutePage';
import { useNavigate } from 'react-router-dom';

const priceLevels = ['A', 'B', 'C', 'D', 'E'];
const cognitoGroups = (loginUser: UserInfo) =>
  loginUser.groups?.includes(CognitoGroup.SystemAdmin)
    ? Object.values(CognitoGroup)
    : [CognitoGroup.Customer, CognitoGroup.User, CognitoGroup.Admin];

type PatchUserFormData = {
  priceLevel: string;
  groups?: CognitoGroup[];
};

// priceLevel isn't required (no .required()) to match the backend (PatchUserRequest.priceLevel is @IsOptional):
// a customer with no price level set yet must still be patchable (e.g. to change their roles) without being
// forced to pick one first. .defined() only rejects `undefined`, not '' — the field's runtime value is always
// a string (defaultValues seeds it with ''), so this keeps the yup-inferred type aligned with PatchUserFormData.
const schema = yup.object({
  priceLevel: yup.string().defined(),
  groups: yup.array(yup.mixed<CognitoGroup>().oneOf(Object.values(CognitoGroup)).required()).optional(),
});

export const UserManagement = () => {
  const navigate = useNavigate();

  const { userSearchCriteria, setIsLoading } = useSearchContext();

  const { userInfo: loginUser, logout } = useUserContext();

  const [user, setUser] = useState<Nullable<UserInfo>>(null);
  const [error, setError] = useState<Nullable<Error>>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const {
    data: users,
    refetch,
    isLoading,
    isFetching,
  } = useUserSearchQuery({
    email: userSearchCriteria?.email,
    onError: (error: Error) => {
      setError(error);
      if (isAuthError(error)) {
        logout().finally(() => {
          navigate(ROUTES.signin);
        });
      }
    },
  });

  const { patchUser } = usePatchUser({
    onSuccess: async () => {
      setSuccessMessage('User was updated successfully');
    },
    onError: (error: Error) => {
      setError(error);
      if (isAuthError(error)) {
        logout().finally(() => {
          navigate(ROUTES.signin);
        });
      }
    },
  });

  useEffect(() => {
    if (userSearchCriteria && userSearchCriteria.email) {
      refetch();
    }
  }, [userSearchCriteria, refetch]);

  // Only isFetching, not isLoading: the search query is `enabled: false` (it runs on demand via refetch()), and
  // react-query v4 has no "not yet run" status for a disabled query — `isLoading` (status === 'loading') is stuck
  // `true` until the first search completes. Feeding that into the shared SearchContext (which also drives the
  // product search box on other pages) made the Search button spin/grey out on landing on this page, before any
  // search was made, and left it stuck for the whole app until a customer search was actually performed.
  useEffect(() => setIsLoading(isFetching), [isFetching, setIsLoading]);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting, isValid, dirtyFields },
    setValue,
    // reset,
  } = useForm<PatchUserFormData>({
    defaultValues: {
      priceLevel: '',
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: PatchUserFormData) => {
    if (user) {
      setSuccessMessage('');
      setError(null);
      patchUser({
        userId: user.id,
        patchUserDTO: {
          ...data,
        },
      });
    }
  };

  useEffect(() => {
    if (users && users.length > 0) {
      setUser(users[0]);
      setValue('priceLevel', users[0].priceLevel || '');
      setValue('groups', users[0].groups || []);
    } else setUser(null);
  }, [users, setValue]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          <Typography component="h1" variant="h5">
            Customer Management
          </Typography>
        </Box>
        {!user && !userSearchCriteria && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, marginTop: 1 }}>
            <Typography component="h6" variant="subtitle1">
              Please provide user's email and perform search
            </Typography>
          </Box>
        )}
        {!user && userSearchCriteria && !isLoading && !isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, marginTop: 1 }}>
            <Typography component="h6" variant="subtitle1">
              No customer/user matching the search email
            </Typography>
          </Box>
        )}
        {user && (
          <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Username:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                </Box>
              </Grid>
              {/* email */}
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Email:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {user.email}
                  </Typography>
                </Box>
              </Grid>
              {/* phone */}
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Phone:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {user.phoneNumber}
                  </Typography>
                </Box>
              </Grid>
              {/* address */}
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Address:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {user.address}
                  </Typography>
                </Box>
              </Grid>
              {/* priceLevel */}
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Price Level:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="priceLevel"
                  control={control}
                  defaultValue={user?.priceLevel}
                  render={({ field }) => (
                    <Select
                      {...field}
                      // value={user.priceLevel}
                      label=""
                      size="small"
                      displayEmpty={true}
                      error={Boolean(errors.priceLevel)}
                      // onChange={(e) => setValue('priceLevel', e.target.value as string)}
                      // helperText={errors.priceLevel?.message || ''}
                    >
                      {priceLevels.map((level) => (
                        <MenuItem key={`price-level-${level}`} value={level}>
                          {level}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
              {/* groups */}
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Roles:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="groups"
                  control={control}
                  defaultValue={user?.groups || []}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label=""
                      aria-label="Roles"
                      size="small"
                      displayEmpty={true}
                      error={Boolean(errors.groups)}
                      multiple={true}
                      // Selected values default to a comma-joined string, which reads as one blob and doesn't show
                      // which roles are actually selected without opening the dropdown; chips make each one distinct.
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as CognitoGroup[]).map((group) => (
                            <Chip key={`role-chip-${group}`} label={group} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {loginUser &&
                        cognitoGroups(loginUser).map((group) => (
                          <MenuItem key={`cognito-group-${group}`} value={group}>
                            <Checkbox
                              checked={(field.value || []).includes(group)}
                              size="small"
                              inputProps={{ 'aria-label': group }}
                            />
                            {group}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </Grid>
              {error && (
                <Grid item xs={12}>
                  <Typography component="h4" variant="body2" color="error">
                    {error.message}
                  </Typography>
                </Grid>
              )}
              {successMessage && (
                <Grid item xs={12}>
                  <Typography component="h4" variant="body2" color="success" sx={{ color: 'blue' }}>
                    {successMessage}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <LoadingButton
              loading={isSubmitting}
              loadingPosition="end"
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, textTransform: 'none' }}
              disabled={!isDirty || isSubmitting || !isValid || Object.keys(dirtyFields).length === 0}
              disableElevation
              endIcon={<CircularProgress size={isSubmitting ? 20 : 0} />}
            >
              Submit
            </LoadingButton>
          </Box>
        )}
      </Box>
    </Container>
  );
};
